import React from "react";
import { FadingWord } from "./FadingWord";
import type { HtmlNode } from "./HtmlNode";

const VOID_HTML_TAGS = new Set<string>([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const isVoidTag = (tagName: string): boolean => VOID_HTML_TAGS.has(tagName);

const isAllowedAttr = (tagName: string, attrName: string): boolean => {
  if (attrName === "class" || attrName === "id") return true;
  if (attrName.startsWith("data-") || attrName.startsWith("aria-")) return true;
  if (tagName === "a")
    return (
      attrName === "href" ||
      attrName === "target" ||
      attrName === "rel" ||
      attrName === "title"
    );
  return false;
};

const buildElementProps = (
  tagName: string,
  attrs: Record<string, string> | undefined,
  isHidden: boolean,
): Record<string, unknown> => {
  const baseProps: Record<string, unknown> = {};
  if (attrs) {
    Object.entries(attrs).forEach(([key, value]) => {
      if (!isAllowedAttr(tagName, key)) return;
      if (key === "class") {
        baseProps.className = value;
      } else {
        baseProps[key] = value;
      }
    });
  }

  if (!isHidden) return baseProps;

  return {
    ...baseProps,
    className: [baseProps.className as string | undefined, "hidden"]
      .filter(Boolean)
      .join(" "),
  };
};

interface FadingHtmlNodeProps {
  currentTokenIndex: number;
  node: HtmlNode;
}

export const FadingHtmlNode = ({
  node,
  currentTokenIndex,
}: FadingHtmlNodeProps): JSX.Element | null => {
  if (node.type === "text") {
    const words = node.content.match(/\S+\s*/g) || [];
    const hasLeadingSpace = node.content.startsWith(" ");
    return (
      <>
        {hasLeadingSpace && <span> </span>}
        {words.map((word, idx) => {
          const wordPosition = (node.wordStart || 0) + idx;
          return (
            <FadingWord
              key={wordPosition}
              isVisible={wordPosition < currentTokenIndex}
            >
              {word}
            </FadingWord>
          );
        })}
      </>
    );
  }

  if (node.type === "element") {
    const tagName = node.tag || "span";
    const isHidden = (node.wordStart || 0) >= currentTokenIndex;
    const elementProps = buildElementProps(tagName, node.attrs, isHidden);

    if (isVoidTag(tagName)) {
      return React.createElement(tagName, elementProps);
    }

    const children = node.children?.map(child => (
      <FadingHtmlNode
        key={`${tagName}-${child.type}-${child.wordStart ?? 0}-${child.content.length}`}
        node={child}
        currentTokenIndex={currentTokenIndex}
      />
    ));

    return React.createElement(tagName, elementProps, children);
  }

  return null;
};
